import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { rolesApi, UserPermission, Role } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { MODULES, MODULE_ALIASES } from '../constants/modules';

const buildDefaultPermissions = (): UserPermission[] =>
  MODULES.map(module => ({
    module: module.id,
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
  }));

const initialFormData = {
  name: '',
  description: '',
  permissions: buildDefaultPermissions(),
};

function mergePermissions(apiPermissions: UserPermission[]): UserPermission[] {
  const map = new Map<string, UserPermission>();
  for (const p of apiPermissions) {
    const moduleId = MODULE_ALIASES[p.module] || p.module;
    map.set(moduleId, { ...p, module: moduleId });
  }
  return MODULES.map(m =>
    map.get(m.id) || { module: m.id, can_view: false, can_add: false, can_edit: false, can_delete: false },
  );
}

function getApiErrorMessage(err: any, fallback: string): string {
  return err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || fallback;
}

export default function RoleManagement() {
  const { user: authUser, loading: authLoading } = useAuth();
  const companyId = authUser?.company?.id;

  const [roles, setRoles] = useState<Role[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchRoles = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await rolesApi.getAll(companyId);
      setRoles(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!authLoading && companyId) fetchRoles();
  }, [authLoading, companyId, fetchRoles]);

  const handleOpen = () => {
    setFormData(initialFormData);
    setSelectedId(null);
    setError(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (moduleId: string, permission: keyof UserPermission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p =>
        p.module === moduleId ? { ...p, [permission]: !p[permission] } : p,
      ),
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Role name is required';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    if (!companyId) { setError('Company context not found.'); return; }

    try {
      setError(null);
      setSaving(true);

      if (selectedId) {
        const existingRole = roles.find(r => r.id === selectedId);
        const userCount = existingRole?._count?.users || 0;
        if (userCount > 0) {
          if (!window.confirm(`This will update permissions for ${userCount} user(s) assigned to this role. Continue?`)) {
            setSaving(false);
            return;
          }
        }
        await rolesApi.update(companyId, selectedId, {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
        });
        setSuccess('Role updated successfully!');
      } else {
        await rolesApi.create(companyId, {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
        });
        setSuccess('Role created successfully!');
      }

      setOpen(false);
      fetchRoles();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save role'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (role: Role) => {
    setSelectedId(role.id);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: mergePermissions(role.permissions),
    });
    setError(null);
    setOpen(true);
  };

  const handleDelete = async (role: Role) => {
    const userCount = role._count?.users || 0;
    const msg = userCount > 0
      ? `Delete role "${role.name}"? ${userCount} user(s) will be unlinked (their current permissions will remain).`
      : `Delete role "${role.name}"?`;
    if (!window.confirm(msg)) return;
    if (!companyId) { setError('Company context not found.'); return; }
    try {
      setError(null);
      await rolesApi.delete(companyId, role.id);
      setSuccess('Role deleted successfully!');
      fetchRoles();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete role');
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar open={!!error && !open} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">{success}</Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Role Management</Typography>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add New Role
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length > 0 ? (
                roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Typography fontWeight="medium">{role.name}</Typography>
                    </TableCell>
                    <TableCell>{role.description || '-'}</TableCell>
                    <TableCell>
                      <Chip label={role._count?.users || 0} size="small" color={role._count?.users ? 'primary' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(role)} color="primary" title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(role)} color="error" title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No roles defined yet. Click "Add New Role" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Role Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedId ? 'Edit Role' : 'Add New Role'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Role Name" name="name"
                value={formData.name} onChange={handleInputChange}
                required
                placeholder="e.g. Production Manager"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Description" name="description"
                value={formData.description} onChange={handleInputChange}
                placeholder="Brief description of this role"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Permissions</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Define what users with this role can access.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Button
                  size="small" variant="outlined"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    permissions: MODULES.map(m => ({
                      module: m.id, can_view: true, can_add: true, can_edit: true, can_delete: true,
                    })),
                  }))}
                >
                  Select All
                </Button>
                <Button
                  size="small" variant="outlined" color="inherit"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    permissions: buildDefaultPermissions(),
                  }))}
                >
                  Deselect All
                </Button>
              </Box>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Module</TableCell>
                      <TableCell align="center">View</TableCell>
                      <TableCell align="center">Add</TableCell>
                      <TableCell align="center">Edit</TableCell>
                      <TableCell align="center">Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MODULES.map((module) => {
                      const permission = formData.permissions.find(p => p.module === module.id)
                        || { module: module.id, can_view: false, can_add: false, can_edit: false, can_delete: false };

                      return (
                        <TableRow key={module.id}>
                          <TableCell>{module.label}</TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_view} onChange={() => handlePermissionChange(module.id, 'can_view')} color="primary" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_add} onChange={() => handlePermissionChange(module.id, 'can_add')} color="primary" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_edit} onChange={() => handlePermissionChange(module.id, 'can_edit')} color="primary" size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_delete} onChange={() => handlePermissionChange(module.id, 'can_delete')} color="primary" size="small" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : selectedId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
